import { parse_api_data } from './panel_register_helpers';
import { normalize_user_role } from '@/utils/system_constants/constant';

export function create_empty_form(role = '', role_group_id = '') {
  const form = empty_form_fields();
  form.role = role;
  form.role_group_id = role_group_id;
  return form;
}

function empty_form_fields() {
  const fields = Object.fromEntries([]);
  fields.user_id = null;
  fields.first_name = '';
  fields.last_name = '';
  fields.email = '';
  fields.original_email = '';
  fields.phone_number = '';
  fields.role = '';
  fields.role_id = '';
  fields.role_group_id = '';
  return fields;
}

export function validate_panel_form(form = {}, is_edit = false) {
  const rules = is_edit ? edit_rules() : create_rules();
  const failed_rule = rules.find((rule) => !rule.skip?.(form) && !rule.is_valid(form));
  return failed_rule ? failed_rule.message : '';
}

export function panel_error_message(error, fallback) {
  const response_data = parse_api_data(error?.response?.data);
  const messages = [response_data.message, response_data.data, error?.message, fallback];
  return first_value(messages);
}

export function create_payload(form = {}) {
  return create_panel_payload(form);
}

function create_panel_payload(form = {}) {
  const payload = Object.fromEntries([]);
  payload.first_name = clean_value(form.first_name);
  payload.last_name = clean_value(form.last_name);
  payload.email = clean_value(form.email);
  payload.phone_number = clean_value(form.phone_number);
  payload.role = form.role;
  assign_project_id(payload, form);
  if (!role_is_system_admin(form.role)) {
    payload.group_id = Number(form.role_group_id);
  }
  return payload;
}

export function update_payload(form = {}) {
  const payload = Object.fromEntries([]);
  payload.user_id = form.user_id;
  payload.first_name = clean_value(form.first_name);
  payload.last_name = clean_value(form.last_name);
  payload.phone_number = clean_value(form.phone_number);
  payload.role_id = Number(form.role_id);
  assign_project_id(payload, form);
  if (!form.is_system_admin) {
    payload.group_id = Number(form.role_group_id);
  }

  if (email_has_changed(form)) {
    payload.email = clean_value(form.email);
  }

  return payload;
}

export function assert_success(response_data, fallback) {
  const data = parse_api_data(response_data);
  if (data?.status && data.status !== 'success') {
    throw new Error(data.message || fallback);
  }
  return data;
}

function create_rules() {
  return base_rules().concat([
    {
      is_valid: (form) => Boolean(form.role),
      message: 'Role is required.'
    },
    {
      is_valid: (form) => Boolean(form.role_group_id),
      skip: (form) => role_is_system_admin(form.role),
      message: 'Region is required.'
    }
  ]);
}

function edit_rules() {
  return base_rules().concat([
    {
      is_valid: (form) => Boolean(form.role_id),
      message: 'Role is required.'
    },
    {
      is_valid: (form) => Boolean(form.role_group_id),
      skip: (form) => Boolean(form.is_system_admin),
      message: 'Region is required.'
    }
  ]);
}

function base_rules() {
  return [
    {
      is_valid: (form) => Boolean(clean_value(form.first_name)),
      message: 'First name is required.'
    },
    {
      is_valid: (form) => has_valid_name(form.first_name),
      message: 'First name must be at least 2 letters and cannot contain numbers.'
    },
    {
      is_valid: (form) => Boolean(clean_value(form.last_name)),
      message: 'Last name is required.'
    },
    {
      is_valid: (form) => has_valid_name(form.last_name),
      message: 'Last name must be at least 2 letters and cannot contain numbers.'
    },
    {
      is_valid: (form) => Boolean(clean_value(form.email)),
      message: 'Email is required.'
    },
    {
      is_valid: (form) => is_valid_email(form.email),
      message: 'Please enter a valid email address.'
    },
    {
      is_valid: (form) => Boolean(clean_value(form.phone_number)),
      message: 'Phone number is required.'
    },
    {
      is_valid: (form) => is_valid_phone_number(form.phone_number),
      message: 'Phone number must be exactly 10 digits.'
    }
  ];
}

function clean_value(value) {
  return String(value || '').trim();
}

function assign_project_id(payload, form = {}) {
  const project_id = Number(form.project_id);
  if (Number.isFinite(project_id) && project_id > 0) {
    payload.project_id = project_id;
  }
}

function email_has_changed(form = {}) {
  return clean_value(form.email).toLowerCase() !== clean_value(form.original_email).toLowerCase();
}

function role_is_system_admin(role = '') {
  return normalize_user_role(role) === 'System Admin';
}

function first_value(values = []) {
  const value = values.find((item) => item !== undefined && item !== null && item !== '');
  return value || '';
}

function is_valid_email(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean_value(email));
}

function is_valid_phone_number(phone_number) {
  return /^\d{10}$/.test(clean_value(phone_number));
}

function has_valid_name(name) {
  return /^[A-Za-z][A-Za-z\s'-]{1,49}$/.test(clean_value(name));
}
